import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { analysisApi, pairAnalysisCacheKey } from '../api/AnalysisApi';
import { useUserAccounts } from './UserAccountsContext';
import { expandPairAnalysis, resolveTimeFrame, } from '../utils/pairAnalysis';
const DEFAULT_TIMEZONE = 'Australia/Sydney';
const PairAnalysisContext = createContext(undefined);
export const usePairAnalysis = () => {
    const context = useContext(PairAnalysisContext);
    if (!context) {
        throw new Error('usePairAnalysis must be used within PairAnalysisProvider');
    }
    return context;
};
export const PairAnalysisProvider = ({ children }) => {
    const { selectedAccountId } = useUserAccounts();
    const [response, setResponse] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const [timeFrame, setTimeFrame] = useState('month');
    const [customRange, setCustomRange] = useState({});
    const [metric, setMetric] = useState('openOrders');
    const [hiddenPairs, setHiddenPairs] = useState([]);
    const [drawdown, setDrawdown] = useState(undefined);
    const [drawdownLoading, setDrawdownLoading] = useState(false);
    const [drawdownError, setDrawdownError] = useState(undefined);
    const cache = useRef(new Map());
    // Guards against a slow earlier request overwriting a newer one.
    const activeRequest = useRef(0);
    const range = useMemo(() => resolveTimeFrame(timeFrame, new Date(), customRange), [timeFrame, customRange]);
    // A custom frame with no dates chosen yet has nothing to query.
    const rangeReady = timeFrame !== 'custom' || (!!customRange.start && !!customRange.end);
    const loadAnalysis = useCallback(async (force) => {
        if (!selectedAccountId || !rangeReady)
            return;
        const key = pairAnalysisCacheKey(selectedAccountId, metric, timeFrame, range);
        if (force) {
            cache.current.delete(key);
        }
        else {
            const cached = cache.current.get(key);
            if (cached) {
                setResponse(cached);
                setError(undefined);
                setLoading(false);
                return;
            }
        }
        const requestId = ++activeRequest.current;
        setLoading(true);
        setError(undefined);
        try {
            const result = await analysisApi.getPairAnalysis(selectedAccountId, metric, range, DEFAULT_TIMEZONE);
            cache.current.set(key, result);
            if (requestId !== activeRequest.current)
                return;
            setResponse(result);
        }
        catch (err) {
            if (requestId !== activeRequest.current)
                return;
            console.error('Error loading pair analysis:', err);
            setError(err.message || 'Failed to load pair analysis');
        }
        finally {
            if (requestId === activeRequest.current)
                setLoading(false);
        }
    }, [selectedAccountId, metric, timeFrame, range, rangeReady]);
    const loadDrawdown = useCallback(async () => {
        if (!selectedAccountId)
            return;
        setDrawdownLoading(true);
        setDrawdownError(undefined);
        try {
            setDrawdown(await analysisApi.getDrawdownInputs(selectedAccountId));
        }
        catch (err) {
            console.error('Error loading drawdown inputs:', err);
            setDrawdownError(err.message || 'Failed to load drawdown');
        }
        finally {
            setDrawdownLoading(false);
        }
    }, [selectedAccountId]);
    useEffect(() => {
        loadAnalysis(false);
    }, [loadAnalysis]);
    useEffect(() => {
        loadDrawdown();
    }, [loadDrawdown]);
    // Cached responses belong to the account they were fetched for.
    useEffect(() => {
        cache.current.clear();
    }, [selectedAccountId]);
    const allPairs = useMemo(() => (response?.pairs ?? []).map((p) => p.symbol).sort(), [response]);
    useEffect(() => {
        setHiddenPairs((prev) => prev.filter((symbol) => allPairs.includes(symbol)));
    }, [allPairs]);
    const analysis = useMemo(() => (response ? expandPairAnalysis(response, hiddenPairs) : { points: [], pairs: [] }), [response, hiddenPairs]);
    const togglePair = useCallback((symbol) => {
        setHiddenPairs((prev) => prev.includes(symbol) ? prev.filter((s) => s !== symbol) : [...prev, symbol]);
    }, []);
    const showAllPairs = useCallback(() => setHiddenPairs([]), []);
    const hideAllPairs = useCallback(() => setHiddenPairs(allPairs), [allPairs]);
    const refresh = useCallback(async () => {
        await Promise.all([loadAnalysis(true), loadDrawdown()]);
    }, [loadAnalysis, loadDrawdown]);
    const value = {
        loading,
        error,
        timeFrame,
        setTimeFrame,
        customRange,
        setCustomRange,
        metric,
        setMetric,
        allPairs,
        hiddenPairs,
        togglePair,
        showAllPairs,
        hideAllPairs,
        analysis,
        drawdown,
        drawdownLoading,
        drawdownError,
        refresh,
    };
    return (_jsx(PairAnalysisContext.Provider, { value: value, children: children }));
};
